import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";

import { TextInput } from "components/common/TextInput";
import { JANZU_URL } from "config/config";
import { ConfidenceSlider } from "features/confidenceSlider/ConfidenceSlider";
import { getTotalClasses } from "features/confidenceSlider/utils";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { Detector, type CreateDetectorInput } from "types/detectors";

interface DemoSettingsProps {
  detector: Detector | undefined;
  handleHasThresholdError?: (value: boolean) => void;
}

export function DemoSettings(props: DemoSettingsProps) {
  const theme = useTheme();

  const { detector, handleHasThresholdError } = props;
  const [isSettingsOpen, setIsSettingsOpen] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(detector?.confidence_threshold);
  const isMD = useMediaQuery(theme.breakpoints.up(770));
  const [isValidThreshold, setIsValidThreshold] = useState(true);

  useEffect(() => {
    if (detector) {
      setConfidenceThreshold(detector.confidence_threshold);
    }
  }, [detector]);

  // used for showing, hiding, and positioning the popover

  // set up the patchPredictor function
  const { patch: updateDetector } = useFetchCustom<Detector>(`${JANZU_URL}device-api/predictors/${detector?.id}`);

  // updates the confidence threshold value of the selected predictor.
  // changes the value in the predictors array instead of refetching everything.
  const patchPredictorCallback = useCallback(
    async (input: CreateDetectorInput) => {
      await updateDetector(input).then((result) => {
        // update the threshold in our predictor cache
        if (detector) {
          detector.confidence_threshold = result.confidence_threshold;
        }
      });
    },
    [updateDetector, detector]
  );

  // called when slider has stopped moving (mouse up)
  // todo: still a little too sensitive. make it more robust
  function updatePredictorConfidence(value: number | Array<number>) {
    if (typeof value === "number") {
      setConfidenceThreshold(value);
      const patchPredictorInput: CreateDetectorInput = {
        confidence_threshold: value,
      };
      patchPredictorCallback(patchPredictorInput);
    }
  }

  // Apply only for mobile version
  const handleSetValidThreshold = (value: boolean) => {
    setIsValidThreshold(value);
    if (handleHasThresholdError) handleHasThresholdError(!value);
  };

  const handleChangeValidateThreshold = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;

    // Validate the input to be a number and within the specified range
    if (/^\d*\.?\d*$/.test(value)) {
      const numericValue = parseFloat(value);
      if (!Number.isNaN(numericValue) && numericValue >= 0.1 && numericValue <= 1) {
        handleSetValidThreshold(true);
        return;
      }
    }
    handleSetValidThreshold(false);
  };

  return (
    <Box>
      {isMD && (
        <Box
          onClick={() => setIsSettingsOpen(!isSettingsOpen)}
          className="my-3 border-b border-b-gray-500 border-solid flex justify-between items-center cursor-pointer"
        >
          <Typography variant="subtitle" fontWeight="semibold" textTransform="uppercase">
            Settings
          </Typography>
          <IconButton>{isSettingsOpen && detector ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}</IconButton>
        </Box>
      )}
      <Box className={`md:block transition-opacity duration-300 ${isSettingsOpen && detector ? "opacity-100 visible" : "opacity-0 invisible"} px-2`}>
        {isMD && detector ? (
          <ConfidenceSlider
            value={confidenceThreshold || 0.5}
            setValue={(ev) => setConfidenceThreshold(ev)}
            setValueCommitted={(ev) => updatePredictorConfidence(ev)}
            hideTradeoffCharts={detector.mode !== DetectorModes.BINARY}
            totalClasses={getTotalClasses(detector)}
          />
        ) : (
          <Box>
            <Typography
              variant="detail"
              align="center"
              className="w-full"
              sx={{ display: ["block", "none"] }}
            >
              Confidence Threshold
            </Typography>
            <TextInput
              error={!isValidThreshold}
              type="number"
              value={confidenceThreshold}
              InputProps={{
                slotProps: {
                  input: {
                    min: 0.1,
                    max: 1,
                    step: 0.1,
                  },
                },
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setConfidenceThreshold(parseFloat(e.target.value));
                handleChangeValidateThreshold(e);
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
